import { Button } from "@repo/ui/components/Base/Button";
import { type UserImportData, UsersPreviewTable } from "../UsersPreviewTable";
import {
  type UserImportBindingConfig,
  userImportService,
} from "@/services/userImportService";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { useState } from "react";
import type { UserDuplicate } from "@repo/types";
import { UsersDuplicateTable } from "../UsersDuplicateTable";

export interface UserImportPreviewStepFormProps {
  fileId: string;
  tenantId: string;
  bindingConfig: UserImportBindingConfig;
  duplicates: UserDuplicate[];
  previews: UserImportData[];
  totalRecords: number;
  onCancel: () => void;
  onNext: () => void;
  setUnprocessedUsersData: (usersData: UserImportData[]) => void;
  setTotalRecords: (number: number) => void;
  setImportedRecords: (number: number) => void;
}

export const UserImportPreviewStepForm = ({
  fileId,
  tenantId,
  bindingConfig,
  duplicates,
  previews,
  totalRecords,
  onCancel,
  onNext,
  setUnprocessedUsersData,
  setTotalRecords,
  setImportedRecords,
}: UserImportPreviewStepFormProps) => {
  const [loading, setLoading] = useState(false);
  const [selectedDuplicates, setSelectedDuplicates] = useState<string[]>([]);

  return (
    <>
      <DialogBody className="flex flex-col gap-y-4 pb-0">
        {!!duplicates.length && (
          <>
            <p className="text-slate-800 text-base leading-5 py-3">
              The following {duplicates.length}{" "}
              {duplicates.length === 1 ? "duplicate was" : "duplicates were"}{" "}
              found with{" "}
              {duplicates.length === 1 ? "a different role" : "different roles"}
              . Pick the users that should be imported and overwritten:
            </p>

            <div className="flex flex-col">
              <UsersDuplicateTable
                duplicates={duplicates}
                selection={selectedDuplicates}
                onSelectionChange={setSelectedDuplicates}
              />
            </div>
          </>
        )}

        <p className="text-slate-800 text-base leading-5 py-3">
          Preview {previews.length} of {totalRecords} records
        </p>

        <div className="flex flex-col">
          <UsersPreviewTable
            users={previews}
            roleOverrides={duplicates}
            selectedRoleOverrides={selectedDuplicates}
          />
        </div>
      </DialogBody>
      <DialogFooter className="flex flex-row justify-between">
        <Button
          variant="destructive"
          type="button"
          disabled={loading}
          onClick={onCancel}
        >
          Back
        </Button>
        <Button
          type="button"
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            const {
              importedRecords,
              previews: unprocessedUsers,
              totalRecords,
            } = await userImportService.import({
              fileId,
              tenantId,
              bindConfig: bindingConfig,
              overwriteDuplicates: selectedDuplicates,
            });
            setLoading(false);

            setTotalRecords(totalRecords);
            setImportedRecords(importedRecords);
            setUnprocessedUsersData(unprocessedUsers ?? []);
            onNext();
          }}
        >
          Import
        </Button>
      </DialogFooter>
    </>
  );
};
